/**
 * A file's type from its first bytes, never its MIME type or name: phone pickers (WhatsApp, Gmail,
 * Drive on Android; Files on iPhone) often give an empty or generic type, or no extension at all.
 */
export type FileType = 'pdf' | 'jpeg' | 'png' | 'webp' | 'heic' | 'unknown';
export const FILE_TYPES: readonly FileType[] = ['pdf', 'jpeg', 'png', 'webp', 'heic', 'unknown'];

/** HEIF brands iPhones and other cameras write (HEIC images and image sequences). */
const HEIF_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1']);

export function sniffType(bytes: Uint8Array): FileType {
  const ascii = (from: number, to: number) => String.fromCharCode(...bytes.subarray(from, to));
  // A PDF may start with a few bytes of junk before its header (allowed by readers).
  if (new TextDecoder('latin1').decode(bytes.subarray(0, 1024)).includes('%PDF-')) return 'pdf';
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
  if ([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((b, i) => bytes[i] === b)) return 'png';
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'webp';
  if (ascii(4, 8) === 'ftyp' && HEIF_BRANDS.has(ascii(8, 12))) return 'heic';
  return 'unknown';
}

/** Images the OCR path reads. HEIC only where the browser can decode it (Safari; checked on upload). */
/** Files larger than this aren't read (a payslip PDF or photo is far smaller). */
export const MAX_FILE_BYTES = 20 * 1024 * 1024;
export const isImageType = (t: FileType) => t === 'jpeg' || t === 'png' || t === 'webp' || t === 'heic';
